import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGoals } from '../contexts/GoalContext';
import {
    getTasks,
    getTaskById,
    createTask as createTaskStorage,
    updateTask as updateTaskStorage,
    deleteTask as deleteTaskStorage
} from '../services/firebaseStorage';

export const useTasks = (calendarId = null) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { currentUser } = useAuth();
    const { notifyTaskChanged } = useGoals();

    const loadTasks = useCallback(async () => {
        if (!currentUser) {
            setTasks([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const data = await getTasks(currentUser.uid, calendarId);
            setTasks(data);
        } catch (err) {
            console.error('Error loading tasks:', err);
            setError(err.message);
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [currentUser, calendarId]);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    const createTask = async (taskData, taskCalendarId) => {
        if (!currentUser) {
            throw new Error('User must be logged in to create tasks');
        }

        // Use provided calendarId or fall back to hook's calendarId
        const targetCalendarId = taskCalendarId || calendarId;

        if (!targetCalendarId) {
            throw new Error('Calendar ID is required to create a task');
        }

        try {
            const newTask = await createTaskStorage(taskData, currentUser.uid, targetCalendarId);
            setTasks(prev => [...prev, newTask]);
            if (newTask.goalId) {
                notifyTaskChanged();
            }
            return newTask;
        } catch (err) {
            console.error('Error creating task:', err);
            throw err;
        }
    };

    const updateTask = async (id, taskData) => {
        if (!currentUser) {
            throw new Error('User must be logged in to update tasks');
        }

        try {
            const updatedTask = await updateTaskStorage(id, taskData, currentUser.uid);
            setTasks(prev => prev.map(task => task.id === id ? updatedTask : task));
            if (updatedTask.goalId || taskData.goalId) {
                notifyTaskChanged();
            }
            return updatedTask;
        } catch (err) {
            console.error('Error updating task:', err);
            throw err;
        }
    };

    const deleteTask = async (id) => {
        if (!currentUser) {
            throw new Error('User must be logged in to delete tasks');
        }

        try {
            await deleteTaskStorage(id, currentUser.uid);
            const deletedTask = tasks.find(t => t.id === id);
            setTasks(prev => prev.filter(task => task.id !== id));
            if (deletedTask?.goalId) {
                notifyTaskChanged();
            }
        } catch (err) {
            console.error('Error deleting task:', err);
            throw err;
        }
    };

    const refreshTasks = () => {
        loadTasks();
    };

    const getTask = async (id) => {
        if (!currentUser) {
            throw new Error('User must be logged in to get tasks');
        }
        return await getTaskById(id, currentUser.uid);
    };

    return {
        tasks,
        loading,
        error,
        createTask,
        updateTask,
        deleteTask,
        refreshTasks,
        getTask
    };
};